/**
 * Consumer category-browse view for the register screen (step 0).
 *
 * Ports the admin Product Library's category grouping
 * (admin/pages/products/library-categories-view.tsx) to the consumer surface:
 * each in-use category is a collapsible row that lazy-loads its products on
 * expand. Selecting a product advances the registration wizard to the Details
 * step (via the ``onSelect`` prop) rather than navigating to an admin page.
 *
 * One of the three always-available step-0 view layouts (categories / grid /
 * list); the register page remembers the last-used layout in localStorage.
 * Generic - no tenant.slug branches.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Package } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Image } from '@/shared/components/ui/image'
import { CategoryIcon } from '@/admin/components/category-icons'
import {
  useConsumerProductCategories,
  useConsumerProductSearch,
} from '@/consumer/hooks/use-registration'
import type {
  ConsumerProductCategory,
  ProductSearchResult,
} from '@/consumer/hooks/use-registration'

const PRODUCTS_PER_PAGE = 25

// ── Lazy-loaded product rows for one category ──────────────────────────────

function CategoryProductRows({
  slug,
  onSelect,
}: {
  slug: string
  onSelect: (product: ProductSearchResult) => void
}) {
  const { t } = useTranslation()
  const [limit, setLimit] = useState(PRODUCTS_PER_PAGE)
  // Reuse the existing consumer search endpoint scoped to this category slug -
  // no new product-loading path. Empty query = browse the category.
  const { data, isPending, isError } = useConsumerProductSearch('', 1, limit, {
    category: [slug],
  })
  const products = data?.results ?? []
  const total = data?.count ?? 0

  // Gate the empty state on loading first (cached-empty-then-refetch flash).
  if (isPending) {
    return (
      <p className="py-2.5 pl-[46px] pr-3 text-[11px] text-[var(--ink-3)]">
        {t('register.loadingProducts', { defaultValue: 'Loading products...' })}
      </p>
    )
  }

  if (isError) {
    return (
      <p className="py-2.5 pl-[46px] pr-3 text-[11px] text-[var(--status-error)]">
        {t('common.loadError', {
          defaultValue: "Couldn't load this list. Please try again.",
        })}
      </p>
    )
  }

  if (products.length === 0) {
    return (
      <p className="py-2.5 pl-[46px] pr-3 text-[11px] text-[var(--ink-3)]">
        {t('register.noProductsInCategory', {
          defaultValue: 'No products in this category.',
        })}
      </p>
    )
  }

  return (
    <div className="flex flex-col border-t-[0.5px] border-solid border-[var(--rule)]">
      {products.map((p) => (
        <Button
          key={p.uuid}
          type="button"
          variant="ghost"
          data-testid={`consumer-category-product-${p.uuid}`}
          onClick={() => onSelect(p)}
          className="flex h-auto w-full cursor-pointer items-center justify-start gap-3 rounded-none border-b-[0.5px] border-solid border-[var(--rule)] bg-transparent py-2.5 pl-[46px] pr-3 text-left last:border-b-0 hover:bg-[var(--bg-subtle)]"
        >
          <Image
            src={p.image?.file_url}
            thumbnailSrc={p.image?.thumbnail_url}
            alt=""
            className="h-9 w-9 shrink-0 rounded-[4px] bg-[var(--bg-subtle)]"
            fallback={<Package className="h-4 w-4 text-[var(--ink-3)]" />}
          />
          <div className="min-w-0">
            <p className="truncate text-[13px] font-normal text-[var(--ink)]">
              {p.cached_short_name || p.cached_long_name}
            </p>
            <p className="truncate text-[11px] text-[var(--ink-3)]">
              {p.product_template.name}
            </p>
          </div>
        </Button>
      ))}
      {products.length < total && (
        <Button
          type="button"
          variant="ghost"
          onClick={() => setLimit((n) => n + PRODUCTS_PER_PAGE)}
          className="h-auto w-full cursor-pointer justify-start rounded-none bg-transparent py-2.5 pl-[46px] pr-3 text-[11px] uppercase tracking-[0.1em] text-[var(--green)] hover:opacity-70"
        >
          {t('register.loadMore', { defaultValue: 'Load more' })} ({products.length} / {total})
        </Button>
      )}
    </div>
  )
}

// ── One collapsible category group ─────────────────────────────────────────

function CategoryGroup({
  category,
  isExpanded,
  onToggle,
  onSelect,
}: {
  category: ConsumerProductCategory
  isExpanded: boolean
  onToggle: () => void
  onSelect: (product: ProductSearchResult) => void
}) {
  return (
    <div className="overflow-hidden rounded-[8px] border-[0.5px] border-solid border-[var(--rule)] bg-[var(--bg)]">
      <Button
        type="button"
        variant="ghost"
        data-testid={`consumer-category-row-${category.slug}`}
        aria-expanded={isExpanded}
        onClick={onToggle}
        className="flex h-auto w-full cursor-pointer items-center justify-start gap-2.5 rounded-none bg-transparent px-3 py-3 text-left hover:bg-[var(--bg-subtle)]"
      >
        <span className="flex h-5 w-5 items-center justify-center text-[var(--ink-3)]">
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </span>
        <CategoryIcon
          icon={category.icon || 'package'}
          className="h-4 w-4 text-[var(--green)]"
        />
        <span className="flex-1 text-[13px] font-medium text-[var(--ink)]">
          {category.name}
        </span>
        <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[10px] text-[var(--green)]">
          {category.product_count}
        </span>
      </Button>
      {isExpanded && (
        <CategoryProductRows slug={category.slug} onSelect={onSelect} />
      )}
    </div>
  )
}

// ── Main view ──────────────────────────────────────────────────────────────

export function ConsumerCategoriesView({
  onSelect,
}: {
  onSelect: (product: ProductSearchResult) => void
}) {
  const { t } = useTranslation()
  const { data, isPending, isError } = useConsumerProductCategories()
  const categories = data?.categories ?? []
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  function toggle(slug: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  // Gate the empty state on loading first, per the empty-state doctrine.
  if (isPending) {
    return (
      <p className="py-12 text-center text-sm text-[var(--ink-3)]">
        {t('register.loadingProducts', { defaultValue: 'Loading products...' })}
      </p>
    )
  }

  if (isError) {
    return (
      <p className="py-12 text-center text-sm text-[var(--status-error)]">
        {t('common.loadError', {
          defaultValue: "Couldn't load this list. Please try again.",
        })}
      </p>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="py-12 text-center">
        <Package className="mx-auto mb-3 h-10 w-10 text-[var(--ink-3)] opacity-40" />
        <p className="text-sm text-[var(--ink-3)]">
          {t('register.categoriesEmpty', {
            defaultValue: 'No categories available yet',
          })}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {categories.map((category) => (
        <CategoryGroup
          key={category.slug}
          category={category}
          isExpanded={expanded.has(category.slug)}
          onToggle={() => toggle(category.slug)}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}
